import React from 'react';
import { Accordion } from '../ui/accordion';
import { PlayerData, PLAYER_SECTIONS } from '../../types/player';
import { getNestedValue } from '../../utils/playerDataUtils';
import { hasMeaningfulPlayerData } from '../../utils/playerDataUtils';
import { NoDataMessage } from './analysis/NoDataMessage';
import { AnalysisSection } from './analysis/AnalysisSection';
import { useKYPSearch } from '../../lib/kyp-search/kyp-search.context';
import { AnimatePresence, motion } from 'framer-motion';

interface PlayerAnalysisSectionsProps {
  playerData: PlayerData;
}

const formatCategoryName = (name: string) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

export const PlayerAnalysisSections: React.FC<PlayerAnalysisSectionsProps> = ({ playerData }) => {
    const { kypPipelineType, completedCategories } = useKYPSearch();

    if (kypPipelineType === 'parallel_categories') {
        if (completedCategories.length === 0) {
            return <NoDataMessage message="Waiting for category results..." />;
        }

        return (
            <Accordion type="single" collapsible className="w-full">
                <AnimatePresence>
                    {completedCategories.map((category) => (
                        <motion.div
                            key={category.category_name}
                            layout
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <AnalysisSection
                                id={category.category_name}
                                title={formatCategoryName(category.category_name)}
                                data={{
                                    ...category.profile_content,
                                    ordered_article_summaries: category.ordered_article_summaries || []
                                }}
                                hasData={true}
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </Accordion>
        );
    }

  // Check if there's meaningful data to display
  if (!hasMeaningfulPlayerData(playerData)) {
    return <NoDataMessage />;
  }

  return (
    <Accordion type="single" collapsible className="w-full">
      {PLAYER_SECTIONS.map((section) => {
        const sectionData = getNestedValue(playerData, section.path);
        const hasData = !!sectionData;

        return (
          <AnalysisSection
            key={section.id}
            id={section.id}
            title={section.title}
            data={playerData[section.id]}
            hasData={hasData}
          />
        );
      })}
    </Accordion>
  );
};